/** Pixels of scroll before the sticky landing nav uses the opaque bar. */
export const LANDING_NAV_SCROLL_PX = 8;

export type OverflowStyle = { overflowY: string; overflow: string };

type ScrollMetrics = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
};

export function overflowScrolls(style: OverflowStyle): boolean {
  const y = (style.overflowY || style.overflow || "").toLowerCase();
  return y === "auto" || y === "scroll" || y === "overlay";
}

function canScroll(el: ScrollMetrics): boolean {
  return el.scrollHeight > el.clientHeight + 1;
}

/**
 * Prefer the landing scroller when it actually overflows; otherwise walk to the
 * nearest overflow parent; otherwise `document.scrollingElement`.
 */
export function nearestScrollRoot<T extends ScrollMetrics & { parentElement: T | null }>(
  start: T | null,
  prefer: T | null,
  getStyle: (el: T) => OverflowStyle,
  fallback: T | null,
): T | null {
  if (prefer && overflowScrolls(getStyle(prefer)) && canScroll(prefer)) return prefer;
  let node: T | null = start;
  while (node) {
    if (overflowScrolls(getStyle(node)) && canScroll(node)) return node;
    node = node.parentElement;
  }
  return fallback;
}

/** Viewport IO root (`null`) when the scroller is the document, not an inner box. */
export function intersectionObserverRoot<T>(root: T | null, viewportAnchors: Array<T | null | undefined>): T | null {
  if (!root) return null;
  for (const anchor of viewportAnchors) {
    if (anchor && anchor === root) return null;
  }
  return root;
}

export function maxScrollTop(scrollTops: number[]): number {
  let max = 0;
  for (const top of scrollTops) {
    if (top > max) max = top;
  }
  return max;
}

export function scrolledFromOffset(scrollTop: number, threshold = LANDING_NAV_SCROLL_PX): boolean {
  return scrollTop > threshold;
}
